import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Users, CheckCircle2, ExternalLink } from 'lucide-react';
import { useMutation, useI18n, Button } from '@ywwwtseng/react-kit';
import { Typography, openTelegramLink, useShare } from '@ywwwtseng/tma-react-kit';
import * as web3 from '@ywwwtseng/web3';
import * as utils from '@/utils';
import { useShareUrl } from '@/hooks/useShareUrl';
import type { MissionWithInfo } from '@/types';

interface MissionProps {
  mission: MissionWithInfo;
  className?: string;
  onRedeemSuccess?: () => void;
}

export function Mission({ mission, className, onRedeemSuccess }: MissionProps) {
  const { t } = useI18n();
  const share = useShare();
  const { share_url } = useShareUrl();
  const [progress, setProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);


  // 从 localStorage 读取已访问状态
  const getVisitedKey = () => `mission_visited_${mission?.id || ''}`;
  const [hasVisitedLink, setHasVisitedLink] = useState(() => {
    if (typeof window !== 'undefined' && mission?.id) {
      const visited = localStorage.getItem(getVisitedKey());
      return visited === 'true';
    }
    return false;
  });

  const missionInfo = mission?.info;
  const hasRedeemed = mission?.hasRedeemed ?? false;

  const config = mission?.config as any;
  const progressType = config?.progress_type;
  const verificationType = config?.verification_type;


  const currentProgress = missionInfo?.currentProgress ?? 0;
  const redeemedCount = missionInfo?.redeemedCount ?? 0;

  // 如果 missionInfo 还没有加载或 nextMilestone 为 0，尝试从 tiers 配置中获取下一个里程碑
  let nextMilestone = missionInfo?.nextMilestone ?? 0;
  if (nextMilestone === 0 && mission && config?.tiers && Array.isArray(config.tiers) && config.tiers.length > 0) {
    // 查找下一个兑换次数对应的 tier
    const nextRedeemCount = redeemedCount + 1;
    const nextTier = config.tiers.find((t: any) => t.times === nextRedeemCount);
    if (nextTier) {
      nextMilestone = nextTier.target ?? 0;
    } else {
      // 如果找不到，使用第一个 tier 作为后备
      const firstTier = config.tiers[0];
      nextMilestone = firstTier?.target ?? 0;
    }
  }
  const maxTimes = missionInfo?.maxTimes ?? 1;
  const hasRedeemedAll = redeemedCount >= maxTimes;

  // 判断是否已兑换
  const isRedeemed = hasRedeemed || hasRedeemedAll;

  // Mutation - 使用通用的 redeem mutation
  const { mutate: redeemMission, isLoading } = useMutation('mission:redeem', {
    onSuccess: () => {
      onRedeemSuccess?.();
    },
  });

  // 对于 visit 任务，需要用户访问过链接才能兑换
  // 对于其他社交任务（join_channel, join_group），允许点击，由后端验证
  // 对于有 progress_type 的任务（如 invite），使用 missionInfo?.canRedeem
  const canRedeem = verificationType === 'visit'
    ? (missionInfo?.canRedeem ?? true) && hasVisitedLink
    : (missionInfo?.canRedeem ?? true);

  const notRedeemableButton = config?.not_redeemable_button;

  // 进度条动画（有 progress_type 的任务）
  useEffect(() => {
    if (progressType && nextMilestone > 0) {
      const progressPercentage = Math.min((currentProgress / nextMilestone) * 100, 100);
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setProgress(progressPercentage);
        setIsAnimating(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentProgress, nextMilestone, progressType]);

  // 获取任务配置
  const getMissionConfig = () => {
    if (!mission) {
      return {
        titleKey: '' as const,
        descriptionKey: '' as const,
        reward: '0 $MOONINI',
        showProgress: false,
        link: null,
      };
    }

    const showProgress = !!progressType;
    const link = config?.link || null;

    // 如果有 tiers 配置且 missionInfo 有 nextReward，使用 nextReward
    // 否则从 mission.amount 转换为 $MOONINI 数量（用于显示）
    const reward = missionInfo?.nextReward ?? mission.amount ?? '0';
    const { decimals, symbol } = utils.token.parseTokenId(mission.token_id);
    const formattedReward = web3.utils.formatUnits(reward, decimals);

    return {
      titleKey: mission.title_key as any,
      descriptionKey: mission.description_key as any,
      descriptionParams: {
        target: nextMilestone,
        reward: formattedReward,
      },
      reward: `${formattedReward} $${symbol}`,
      showProgress,
      link,
    };
  };

  const missionConfig = getMissionConfig();

  const handleOpenLink = () => {
    if (missionConfig.link) {
      if (verificationType === 'visit') {
        // 打开 X 链接并记录用户已访问
        window.open(missionConfig.link, '_blank');
        // 保存到 localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem(getVisitedKey(), 'true');
        }
        setHasVisitedLink(true);
      } else {
        openTelegramLink(missionConfig.link);
      }
    }
  };

  // 检测用户是否返回页面（用于 visit 任务）
  useEffect(() => {
    if (verificationType === 'visit') {
      // 监听页面可见性变化，当用户返回时检查是否可以兑换
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && hasVisitedLink) {
          // 用户返回页面且已访问过链接，可以兑换
          // hasVisitedLink 已经设置为 true，这里不需要额外操作
        }
      };

      // 监听窗口焦点变化
      const handleFocus = () => {
        if (hasVisitedLink) {
          // 用户返回窗口且已访问过链接
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [verificationType, hasVisitedLink]);

  const handleRedeem = () => {
    redeemMission({ mission_id: mission.id });
  };

  // 处理不可兑换时的按钮操作
  const handleNotRedeemableAction = () => {
    if (!notRedeemableButton) return;

    const { action } = notRedeemableButton;

    if (action === 'share' && share_url) {
      share({
        url: share_url,
        text: t('share.text'),
      });
    }
    // 可以在这里添加其他 action 的处理逻辑
  };

  // 如果已兑换，不显示
  if (isRedeemed) {
    return null;
  }

  // 如果任务不存在，不显示
  if (!mission) {
    return null;
  }

  return (
    <div
      className={clsx(
        'card flex flex-col w-full gap-1.5 p-2 relative',
        className
      )}
    >
      {/* 背景装饰 */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        {progressType ? (
          <>
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full blur-xl" />
            <div className="absolute bottom-0 left-0 w-12 h-12 bg-gradient-to-tr from-purple-400 to-pink-500 rounded-full blur-lg" />
          </>
        ) : (
          <>
            <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full blur-lg" />
            <div className="absolute bottom-0 left-0 w-12 h-12 bg-gradient-to-tr from-pink-400 to-red-500 rounded-full blur-md" />
          </>
        )}
      </div>

      <div className="flex items-start gap-1.5 relative z-10">
        {/* 内容区域 */}
        <div className="flex flex-col flex-1 gap-1 min-w-0">
          {/* Title 和进度条在同一行 */}
          <div className="flex items-center justify-between gap-2">
            {missionConfig.link && !isRedeemed ? (
              <button
                onClick={handleOpenLink}
                className="flex items-center gap-1.5 text-left hover:opacity-80 transition-opacity"
              >
                <Typography size="1" weight={600} i18n={missionConfig.titleKey} />
                <ExternalLink size={14} className="opacity-70" />
              </button>
            ) : (
              <Typography size="1" weight={600} i18n={missionConfig.titleKey} />
            )}
            {missionConfig.showProgress && progressType && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Users size={14} className="opacity-70" />
                <Typography size="1" weight={600}>
                  {currentProgress} / {nextMilestone}
                </Typography>
              </div>
            )}
          </div>

          {/* Description 和按钮在同一行 */}
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <Typography size="1" i18n={missionConfig.descriptionKey} params={missionConfig.descriptionParams} className="opacity-80 flex-1 min-w-0" />

            <div className="flex items-center gap-1 flex-shrink-0">
              {/* 兑换按钮 */}
              {!isRedeemed && (
                <>
                  {canRedeem ? (
                    <Button
                      className="h-6 px-2"
                      disabled={!canRedeem}
                      isLoading={isLoading}
                      onClick={handleRedeem}
                      variant="contained"
                      color="destructive"
                      rounded="full"
                      size="xs"
                    >
                      <Typography size="1" weight={600} className="text-white">
                        {missionConfig.reward}
                      </Typography>
                    </Button>
                  ) : notRedeemableButton ? (
                    <Button
                      className="h-6 px-2"
                      onClick={handleNotRedeemableAction}
                      variant="contained"
                      color="secondary"
                      rounded="full"
                      size="xs"
                    >
                      <Typography size="1" weight={600} className="text-white" i18n={notRedeemableButton.text} />
                    </Button>
                  ) : (
                    <Button
                      className="h-6 px-2"
                      disabled={true}
                      variant="contained"
                      color="destructive"
                      rounded="full"
                      size="xs"
                    >
                      <Typography size="1" weight={600} className="text-white">
                        {missionConfig.reward}
                      </Typography>
                    </Button>
                  )}
                </>
              )}

              {/* 已兑换提示 */}
              {isRedeemed && (
                <div className="flex items-center gap-1 p-1 bg-green-500/10 rounded-lg">
                  <CheckCircle2 size={12} className="text-green-400" />
                  <Typography size="1" weight={600} className="text-green-400" i18n="earn.invite_mission_redeemed" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
